/**
 * Blood test visibility rules for the membership flow.
 *
 * Two blood test products exist in Supabase, keyed by Stripe product ID:
 *   - Baseline (Advanced blood test): BASELINE_BLOOD_TEST_STRIPE_ID
 *   - Follow-up (hidden by default):  FOLLOWUP_BLOOD_TEST_STRIPE_ID
 *
 * Which one a user sees depends on their membership entitlement state.
 */

export const BASELINE_BLOOD_TEST_STRIPE_ID = "prod_T13oM2UxlC4cnn";
export const FOLLOWUP_BLOOD_TEST_STRIPE_ID = "prod_UBQsJztdFTcw1C";

export function isBaselineBloodTest(
  stripeProductId: string | undefined,
): boolean {
  return stripeProductId === BASELINE_BLOOD_TEST_STRIPE_ID;
}

export function isFollowupBloodTest(
  stripeProductId: string | undefined,
): boolean {
  return stripeProductId === FOLLOWUP_BLOOD_TEST_STRIPE_ID;
}

export interface BloodTestVisibilityContext {
  isMember: boolean;
  /** Server-resolved entitlement type — "baseline_blood_test" | "followup_blood_test" | null */
  currentEntitlementType: string | null;
  /** URL flag `?showAllBloodTests=true` — unlocks the follow-up regardless of state. */
  showAllBloodTestsFlag: boolean;
}

/**
 * Decide whether a blood test product should be rendered in the list.
 *
 * - Non-blood-test products → always visible (caller should short-circuit).
 * - showAllBloodTestsFlag → both baseline and follow-up visible.
 * - Non-member → baseline visible, follow-up hidden.
 * - Member with baseline_blood_test entitlement → baseline visible, follow-up hidden.
 * - Member with followup_blood_test entitlement → follow-up visible, baseline hidden.
 * - Member with no current entitlement (all used) → both visible (let them pay or choose).
 */
export function isBloodTestVisible(
  stripeProductId: string | undefined,
  ctx: BloodTestVisibilityContext,
): boolean {
  if (
    !isBaselineBloodTest(stripeProductId) &&
    !isFollowupBloodTest(stripeProductId)
  ) {
    return true;
  }

  if (ctx.showAllBloodTestsFlag) return true;

  if (!ctx.isMember) {
    return isBaselineBloodTest(stripeProductId);
  }

  if (ctx.currentEntitlementType === "baseline_blood_test") {
    return isBaselineBloodTest(stripeProductId);
  }

  if (ctx.currentEntitlementType === "followup_blood_test") {
    return isFollowupBloodTest(stripeProductId);
  }

  // Member with no available entitlement → show both
  return true;
}
